import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { useDashboardStore } from '../../store/dashboardStore';

interface PinChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  onError?: (message: string) => void;
  /** 変更成功後に自動ログアウト（新しいPINで再ログインするため） */
  onLogoutAfterSuccess?: () => void | Promise<void>;
}

export const PinChangeDialog: React.FC<PinChangeDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
  onError,
  onLogoutAfterSuccess,
}) => {
  const { currentUser, updateStaff, allStaff } = useDashboardStore();
  const [pin, setPin] = useState(['', '', '', '']);
  const [show0000Error, setShow0000Error] = useState(false);
  const [showDuplicateError, setShowDuplicateError] = useState(false);
  const [showSamePinError, setShowSamePinError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!open) {
      setPin(['', '', '', '']);
      setShow0000Error(false);
      setShowDuplicateError(false);
      setShowSamePinError(false);
      setShowSuccess(false);
    } else {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [open]);

  const pinStr = pin.join('');

  const handleInputChange = (i: number, v: string) => {
    const digit = v.replace(/\D/g, '').slice(-1);
    const next = [...pin];
    next[i] = digit;
    setPin(next);
    setShow0000Error(false);
    setShowDuplicateError(false);
    setShowSamePinError(false);
    if (digit && i < 3) inputRefs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !pin[i] && i > 0) {
      e.preventDefault();
      inputRefs.current[i - 1]?.focus();
      const next = [...pin];
      next[i - 1] = '';
      setPin(next);
    setShow0000Error(false);
    setShowDuplicateError(false);
    setShowSamePinError(false);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    const next = ['', '', '', ''];
    pasted.split('').forEach((c, i) => { next[i] = c; });
    setPin(next);
    setShow0000Error(false);
    setShowDuplicateError(false);
    setShowSamePinError(false);
    const focusIdx = Math.min(pasted.length, 3);
    inputRefs.current[focusIdx]?.focus();
  };

  const handleSubmit = async () => {
    if (pinStr.length !== 4) return;
    if (pinStr === '0000') {
      setShow0000Error(true);
      return;
    }
    const myStaff = currentUser?.id ? allStaff.find((s) => s.id === currentUser.id) : null;
    if (myStaff?.pin && pinStr === myStaff.pin) {
      setShowSamePinError(true);
      return;
    }
    const duplicateStaff = allStaff.find(
      (s) => s.id !== currentUser?.id && s.isActive && s.pin === pinStr
    );
    if (duplicateStaff) {
      setShowDuplicateError(true);
      return;
    }
    if (!currentUser?.id) {
      onError?.('ユーザー情報が取得できません');
      onOpenChange(false);
      return;
    }
    setLoading(true);
    try {
      const ok = await updateStaff(currentUser.id, { pin: pinStr });
      if (ok) {
        setShowSuccess(true);
        setTimeout(async () => {
          onOpenChange(false);
          await onLogoutAfterSuccess?.();
        }, 2000);
      } else {
        setShowDuplicateError(true);
      }
    } catch {
      setShowDuplicateError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleRetry = () => {
    setPin(['', '', '', '']);
    setShow0000Error(false);
    setShowDuplicateError(false);
    setShowSamePinError(false);
    inputRefs.current[0]?.focus();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm bg-[#00a48d]" overlayClassName="bg-transparent" showCloseButton={false}>
        <DialogHeader className="text-center">
          <DialogTitle className="text-white">PINを変更</DialogTitle>
        </DialogHeader>

        {showSuccess ? (
          <p className="text-center text-white text-base py-4">
            PINを変更しました。ログアウトします。
          </p>
        ) : show0000Error || showDuplicateError || showSamePinError ? (
          <div className="space-y-4 py-2">
            <p className="text-center text-white font-bold py-3 px-4 rounded-lg bg-red-600">
              {show0000Error ? '0000は使用できません。' : showDuplicateError ? 'このPINは使用できません。' : '現在のPINと同じです。'}
            </p>
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={handleClose} className="border-white text-white hover:bg-white/20">
                閉じる
              </Button>
              <Button onClick={handleRetry} className="bg-white text-[#00a48d] hover:bg-white/90">
                もう一度入力する
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* 4桁の入力ボックス（タップでスマホキーボード表示） */}
            <div className="flex justify-center gap-2 py-4">
              {[0, 1, 2, 3].map((i) => (
                <input
                  key={i}
                  ref={(el) => { inputRefs.current[i] = el; }}
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={pin[i]}
                  onChange={(e) => handleInputChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  onPaste={handlePaste}
                  disabled={loading}
                  className="w-14 h-16 text-center text-2xl font-bold rounded-md border-0 outline-none bg-gray-50 focus:border-2 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-200"
                  aria-label={`PIN ${i + 1}桁目`}
                />
              ))}
            </div>

            {/* キャンセル｜変更ボタン（下に配置） */}
            <div className="flex justify-center gap-4 pt-4">
              <Button variant="outline" onClick={handleClose} disabled={loading} className="border-white text-white hover:bg-white/20 disabled:opacity-50">
                キャンセル
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={pinStr.length !== 4 || loading}
                className="min-w-[100px] bg-white text-[#00a48d] hover:bg-white/90 disabled:opacity-50"
              >
                {loading ? '変更中...' : '変更'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
